import React, { useLayoutEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useController, ApplicationView as view } from '@aha-app/mvc';
import { SelectroController } from '@aha-app/selectro-v2';
import type { Item } from '@aha-app/selectro-v2';
import UserAvatar from './components/UserAvatar';
import { resolveAttribute } from '../helpers';

interface UserTriggerItemsProps {
  // To have more control over the items displayed, we can pass items directly.
  items?:
    | {
        value: any;
        text?: string;
        label?: string;
        avatar: string;
        initials: string;
      }[]
    | Item[];
}
// This component handles overflow by checking if the width of the items exceeds the container width.
// When this happens, we switch to avatar-only mode and show as many avatars as fit, minus one space for trailing dots.
const UserTriggerItems: React.FC<UserTriggerItemsProps> = ({ items }) => {
  const controller = useController(SelectroController);
  const { selectedItems, state } = controller;
  const multiple = !controller.isSingleSelect;
  const triggerItems = items || selectedItems;
  // If disabled is not explicitly set, we assume the field is editable.
  const editable =
    typeof state.disabled === 'undefined' ? true : !state.disabled;
  // In some fields, we don't want to render the full item; this var from state.custom controls that.
  const alwaysRenderOnlyAvatars = state.custom.alwaysRenderOnlyAvatars || false;
  const containerRef = useRef<HTMLDivElement>(null);
  const hiddenItemsRef = useRef<HTMLDivElement>(null);
  const [itemsOverflow, setItemsOverflow] = useState(false);
  const [maxAvatars, setMaxAvatars] = useState(5);
  // The switch from full item to avatar-only can take just enough time for a user to notice.
  // We track once we have calculated the overflow to avoid flickering.
  const [overflowCalculated, setOverflowCalculated] = useState(!multiple);

  const handleRemove = (e, item: any) => {
    e.preventDefault();
    const newSelection = controller.selected.filter(
      selectedItem => selectedItem.value !== item.value
    );
    controller.actionSetSelected(newSelection);
    // When removing an item, it's important to call the selectro-v2 select
    // callback so react-in-place or any other component can react to the change and persist it.
    controller.callSelectCallback({
      item,
      // By default, react-in-place does not persist changes immediately on select,
      // this tells it to do so the moment a user clicks on the remove icon.
      persistChange: true,
    });
  };

  useLayoutEffect(() => {
    // To properly measure the overflow, we render the items twice,
    // once hidden for measurement and once visible.
    const resizeObserver = new ResizeObserver(() => {
      // If not multiple, we don't care about overflow.
      if (!multiple || !containerRef.current || !hiddenItemsRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const hiddenItemsWidth = hiddenItemsRef.current.offsetWidth;
      // Avatar width including padding minus one avatar space to show trailing dots.
      setMaxAvatars(Math.floor((containerWidth - 22) / 22));
      // If we are forcing avatar-only mode, we always consider it overflow.
      // We still need to calculate maxAvatars though, so we don't return early.
      setItemsOverflow(
        alwaysRenderOnlyAvatars ? true : hiddenItemsWidth > containerWidth
      );

      if (!overflowCalculated) setOverflowCalculated(true);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    if (hiddenItemsRef.current) {
      resizeObserver.observe(hiddenItemsRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [
    alwaysRenderOnlyAvatars,
    multiple,
    overflowCalculated,
    triggerItems.length,
  ]);

  if (!triggerItems?.length) {
    return <span>{state.text.noneSelected}</span>;
  }

  const renderTriggerItems = (isHidden = false) => {
    // There's no use in rendering more than 20 items for measurement.
    // When not hidden and avatar only is enabled, we show up to a maximum for
    // the amount of avatars that fit minus one for the trailing dots.
    const slicedItems = isHidden
      ? triggerItems.slice(0, 20)
      : triggerItems.slice(0, maxAvatars);

    const showTrailingDots =
      !isHidden && itemsOverflow && triggerItems.length > slicedItems.length;

    return (
      <div
        className={classnames('tw-flex tw-items-center tw-gap-1', {
          'tw-fixed tw-invisible tw-pointer-events-none': isHidden,
        })}
        ref={isHidden ? hiddenItemsRef : null}
      >
        {slicedItems.map(item => (
          <UserAvatar
            editable={editable}
            onRemove={
              !isHidden ? event => handleRemove(event, item) : undefined
            }
            key={`${isHidden ? 'hidden-' : ''}${item.value}`}
            avatar={resolveAttribute(item, 'avatar')}
            text={
              resolveAttribute(item, 'text') || resolveAttribute(item, 'label')
            }
            multiple={multiple}
            initials={resolveAttribute(item, 'initials')}
            onlyAvatar={isHidden ? false : itemsOverflow}
          />
        ))}
        {showTrailingDots && (
          <div className='tw-flex tw-items-center tw-justify-center'>
            <i className='fa-solid fa-ellipsis' />
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={classnames(
        'tw-flex tw-gap-1 tw-items-center tw-w-full tw-max-w-full tw-relative',
        { 'tw-invisible': !overflowCalculated }
      )}
      ref={containerRef}
    >
      {state.text?.preScript && <span>{state.text.preScript}</span>}
      {/* Hidden measurement container -- if not multiple, we don't calculate the overflow */}
      {multiple && renderTriggerItems(true)}
      {/* Visible container */}
      {renderTriggerItems(false)}
    </div>
  );
};

export default view(UserTriggerItems);
